/**
 * Formulário de um contato da agenda.
 *
 * Papel: preenche, valida e grava um contato — os campos de texto, a lista de
 * telefones e os dois botões —, servindo tanto à criação quanto à edição.
 *
 * Como usar: quem monta este componente passa `contato` para editar ou `null`
 * para criar, e deverá dar a ele uma `key` diferente por contato. Sem a `key`,
 * o React reaproveita a mesma instância ao trocar de registro e os campos
 * continuam com o que a pessoa via antes, porque `defaultValues` só é lido na
 * montagem.
 *
 * A validação é a de `novoContatoMailingSchema`, o mesmo schema da Function. O
 * formulário guarda string vazia onde o contrato guarda `null`, e é o schema
 * que converte no envio; escrever a conversão aqui deixaria a regra em dois
 * lugares e ela deixaria de valer para quem chamasse a API direto.
 *
 * A categoria é campo de texto com sugestão, e não escolha fechada: os rótulos
 * em uso descrevem o que cada fornecedor entrega e quase todos servem a um
 * contato só. A lista de sugestão é o que evita `Tapeceiro` e `Tapecaria`
 * passarem a conviver.
 */
import {
  type ContatoMailing,
  type NovoContatoMailing,
  novoContatoMailingSchema,
} from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type FieldPath, useForm } from 'react-hook-form';

import { aplicarErroDaApi } from '@/shared/lib/erro-de-formulario';
import { Button } from '@/shared/ui/button';
import { DialogFooter } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { CamposTelefone } from './campos-telefone';
import { useCategorias, useSalvarContato } from '../hooks/usar-contatos';
import { CONTATO_EM_BRANCO, type ContatoNoFormulario } from '../types';

interface FormularioContatoProps {
  /** O contato a editar, ou `null` para criar um novo. */
  contato: ContatoMailing | null;
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos de topo que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS_DE_TOPO: Record<string, FieldPath<ContatoNoFormulario> | undefined> = {
  nome: 'nome',
  categoria: 'categoria',
  email: 'email',
  endereco: 'endereco',
  observacao: 'observacao',
  telefones: 'telefones',
};

/**
 * Traduz o caminho de campo que a API devolveu num caminho do formulário.
 *
 * O caminho chega como texto — `nome`, ou `telefones.0.numero` para o número da
 * primeira linha — e precisa ser reconhecido antes de virar `setError`, porque
 * um caminho que o formulário não conhece marcaria um campo que não existe e o
 * erro sumiria da tela em silêncio.
 *
 * @param caminho - A chave vinda de `erro.campos`, como a Function a montou.
 * @returns O caminho do formulário, ou `null` quando não corresponde a campo
 *   algum — caso em que a mensagem é mostrada acima dos botões.
 */
function caminhoDeCampo(caminho: string): FieldPath<ContatoNoFormulario> | null {
  const deTopo = CAMPOS_DE_TOPO[caminho];
  if (deTopo !== undefined) return deTopo;

  const emTelefone = /^telefones\.(\d+)\.(tipo|numero|falarCom)$/.exec(caminho);
  if (emTelefone === null) return null;

  const indice = Number(emTelefone[1]);
  if (emTelefone[2] === 'tipo') return `telefones.${indice}.tipo`;
  if (emTelefone[2] === 'numero') return `telefones.${indice}.numero`;
  return `telefones.${indice}.falarCom`;
}

/**
 * Converte o contato vindo da API no formato que o formulário guarda.
 *
 * @param contato - O contato a editar, ou `null` para começar em branco.
 * @returns Os valores iniciais, com `null` trocado por string vazia.
 */
function valoresIniciais(contato: ContatoMailing | null): ContatoNoFormulario {
  if (contato === null) {
    return { ...CONTATO_EM_BRANCO, telefones: [...(CONTATO_EM_BRANCO.telefones ?? [])] };
  }

  return {
    nome: contato.nome,
    categoria: contato.categoria,
    email: contato.email ?? '',
    endereco: contato.endereco ?? '',
    observacao: contato.observacao ?? '',
    telefones: contato.telefones.map((telefone) => ({
      tipo: telefone.tipo,
      numero: telefone.numero,
      falarCom: telefone.falarCom ?? '',
    })),
  };
}

/**
 * O formulário de criação e de edição de contato.
 *
 * @param contato - O contato a editar, ou `null` para criar.
 * @param onSalvo - Chamado depois de a API confirmar a gravação.
 * @param onCancelar - Chamado quando se desiste, sem gravar nada.
 * @returns O formulário completo, com os botões de ação.
 */
export function FormularioContato({ contato, onSalvo, onCancelar }: FormularioContatoProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const categorias = useCategorias();
  const salvar = useSalvarContato();

  const form = useForm<ContatoNoFormulario, unknown, NovoContatoMailing>({
    resolver: zodResolver(novoContatoMailingSchema),
    defaultValues: valoresIniciais(contato),
  });

  const { errors } = form.formState;
  const enviando = salvar.isPending;

  /**
   * Grava o contato e leva o erro da API para o campo que o causou.
   *
   * O erro por campo chega em `erro.campos`, com o caminho no formato que o
   * react-hook-form entende — `telefones.0.numero` marca o número da primeira
   * linha. O que não tem caminho de campo vira a mensagem acima dos botões; sem
   * isso a gravação falharia sem que nada aparecesse na tela.
   */
  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      await salvar.mutateAsync({ id: contato?.id ?? null, dados });
      onSalvo();
    } catch (erro) {
      // O que não coube em campo nenhum — inclusive o `_geral` que a API usa
      // para regra sobre o objeto inteiro — volta como mensagem e aparece acima
      // dos botões, ou o envio falharia sem explicação na tela.
      setErroGeral(aplicarErroDaApi(erro, form, caminhoDeCampo));
    }
  });

  return (
    <form onSubmit={handleSalvar}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="contato-nome">Nome</Label>
          <Input
            id="contato-nome"
            placeholder="Ex: João da Silva"
            autoComplete="off"
            disabled={enviando}
            {...form.register('nome')}
          />
          {errors.nome && (
            <p className="text-sm text-destructive" role="alert">
              {errors.nome.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="contato-categoria">Categoria</Label>
          <Input
            id="contato-categoria"
            list="categorias-em-uso"
            autoComplete="off"
            placeholder="Fornecedor, Buffet…"
            disabled={enviando}
            {...form.register('categoria')}
          />
          <datalist id="categorias-em-uso">
            {(categorias.data ?? []).map((categoria) => (
              <option key={categoria} value={categoria} />
            ))}
          </datalist>
          {errors.categoria && (
            <p className="text-sm text-destructive" role="alert">
              {errors.categoria.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-3">
          <CamposTelefone
            control={form.control}
            erros={errors.telefones}
            desabilitado={enviando}
          />
          {errors.telefones?.root && (
            <p className="text-sm text-destructive" role="alert">
              {errors.telefones.root.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="contato-email">E-mail</Label>
          <Input
            id="contato-email"
            type="email"
            autoComplete="off"
            placeholder="nome@email.com"
            disabled={enviando}
            {...form.register('email')}
          />
          {errors.email && (
            <p className="text-sm text-destructive" role="alert">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="contato-endereco">Endereço</Label>
          <Input
            id="contato-endereco"
            autoComplete="off"
            placeholder="Rua, número, bairro, cidade/UF"
            disabled={enviando}
            {...form.register('endereco')}
          />
          {errors.endereco && (
            <p className="text-sm text-destructive" role="alert">
              {errors.endereco.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="contato-observacao">Observações</Label>
          <Textarea
            id="contato-observacao"
            placeholder="Notas adicionais"
            rows={4}
            disabled={enviando}
            {...form.register('observacao')}
          />
          {errors.observacao && (
            <p className="text-sm text-destructive" role="alert">
              {errors.observacao.message}
            </p>
          )}
        </div>

        {erroGeral !== null && (
          <p className="col-span-full text-sm text-destructive" role="alert">
            {erroGeral}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar contato'}
        </Button>
      </DialogFooter>
    </form>
  );
}
